import { Comment, Typography } from 'antd';
import classNames from 'classnames';
import React, { FC } from 'react';
import {
  FormattedMessage,
  FormattedTime,
  IntlShape,
  useIntl,
} from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  ControlMessage as IControlMesssage,
  MessageSubTypes,
  ProjectConversationNewMember,
} from 'api/conversations/types';
import { ThirdpartyIntegrationType } from 'api/integrations';
import { User } from 'api/models';
import { Caption01, SingleLineEllipsis, Text03 } from 'components/styles';
import { RoutePaths } from 'constant';
import { useExternalIntegrationUserDetails } from 'hooks/use-external-integration-user-details';
import { extname, isImageUrlFromExtension } from 'utils/files';
import { fullName } from 'utils/full-name';
import { UserMessageCommentAvatar } from '../components/comment-avatar';
import { AttachmentVersionMessage } from './attachment-control-message';
import styles from './file-thumbnail.module.css';
import { TaskUpdateFieldRow } from './task-update-field-row';

const { Paragraph } = Typography;

const Date = styled.span`
  display: block;
  ${SingleLineEllipsis};
`;

const CommentWrapper = styled(Comment)`
  ${Caption01};
  ${Text03};
  display: flex;
  margin-bottom: var(--spacing-03);
  position: relative;

  &:hover {
    background: var(--coolest-gray-10);
  }

  .ant-comment-inner {
    padding: var(--spacing-01) var(--spacing-05) var(--spacing-01)
      var(--spacing-02);
  }
`;

const AttachmentCommentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;
  width: 100%;
`;

const AttachmentMetaWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-top: var(--spacing-03);
`;

const AttachmentMetaTextWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: space-between;
  margin-left: var(--spacing-04);
`;

const AttachmentThumbnail = styled.img`
  height: var(--spacing-07);
  width: 50px;
`;

const Flex = styled.div`
  display: flex;
`;

type AuditLogProps = {
  comment: IControlMesssage;
  className?: string;
};

const renderText = (
  comment: IControlMesssage,
  intl: IntlShape,
  userDetails: User,
) => {
  const keyStringArray = [
    comment.messageType,
    comment.messageSubType,
    comment.meta?.changeType,
  ].filter((key) => key);
  const keyForString = keyStringArray.join('.');

  switch (comment.messageSubType) {
    case MessageSubTypes.ProjectFolderAddAttachment:
    case MessageSubTypes.TaskUpdateAttachment:
      return (
        <FormattedMessage
          id={keyForString}
          values={{
            attachmentName: comment.meta.attachmentName,
            changedByUserName: fullName(userDetails),
          }}
        />
      );
    case MessageSubTypes.TaskUpdateField:
      return (
        <TaskUpdateFieldRow
          messageType={comment.messageType}
          messageSubType={comment.messageSubType}
          meta={comment.meta}
          user={userDetails}
        />
      );
    case MessageSubTypes.TaskUpdateList:
      return (
        <FormattedMessage
          id={keyForString}
          values={{
            listName:
              comment.meta.fieldLabel ||
              intl.formatMessage({ id: comment.meta.listName }),
            changedValue: comment.meta.changedValue,
            changedByUserName: fullName(userDetails),
          }}
        />
      );
    case MessageSubTypes.TaskNew:
      if (
        comment.commentMeta?.externalUser?.integrationType ===
        ThirdpartyIntegrationType.Form
      ) {
        return (
          <FormattedMessage
            id={`${keyForString}.FORM`}
            values={{
              changedByUserName:
                comment.commentMeta?.externalUser.name || fullName(userDetails),
            }}
          />
        );
      }

      return (
        <FormattedMessage
          id={keyForString}
          values={{
            changedByUserName: fullName(userDetails),
          }}
        />
      );

    case MessageSubTypes.BulkTasksNew:
      return (
        <FormattedMessage
          id={keyForString}
          values={{
            changedByUserName: fullName(comment.user),
            subtasksLength: comment.meta.totalTasks,
          }}
        />
      );
    case MessageSubTypes.TaskUpdatePhase:
      return (
        <FormattedMessage
          id={keyForString}
          values={{
            changedByUserName: fullName(userDetails),
            hasFrom: Boolean(comment.meta.oldValue),
            from: comment.meta.oldValue?.label,
            to: comment.meta.newValue?.label,
          }}
        />
      );
    case MessageSubTypes.TaskUpdateApproval:
      return (
        <FormattedMessage
          id={keyForString}
          values={{
            changeType: comment.meta.changeType,
            currentStatus: comment.meta.currentStatus,
            requestedBy: comment.meta.requestedByUserName,
            requestedTo: comment.meta.requestedToUsers?.join(', '),
          }}
        />
      );
    case MessageSubTypes.TaskUpdateDependentTask:
      return (
        <FormattedMessage
          id={keyForString}
          values={{
            changeType: comment.meta.changeType,
            changedByUserName: comment.meta.changedByUserName,
            taskName: comment.meta.changedObject.taskName,
          }}
        />
      );
    case MessageSubTypes.TaskUpdateSubTasks:
      return (
        <FormattedMessage
          id={keyForString}
          values={{
            changeType: comment.meta.changeType,
            changedByUserName: comment.meta.changedByUserName,
            taskName: comment.meta.changedObject.taskName,
          }}
        />
      );
    case MessageSubTypes.TaskUpdateAssignee:
    case MessageSubTypes.TaskUpdateFollowers:
      return (
        <FormattedMessage
          id={keyForString}
          values={{
            changeType: comment.meta.changeType,
            changedByUserName: fullName(userDetails),
            squad:
              comment.meta.changedObject.userName ||
              comment.meta.changedObject.teamName,
            label: comment.meta.fieldLabel,
          }}
        />
      );
    case MessageSubTypes.ProjectConversationNew:
      return <FormattedMessage id={keyForString} />;

    case MessageSubTypes.ProjectConversationNewMember: {
      const remainingMembers = comment.meta?.users.length - 1;

      return (
        <FormattedMessage
          id={keyForString}
          values={{
            remainingMembers,
            convName:
              comment.commentContext?.associatedProjectConversation
                ?.conversationName ?? '',
          }}
        />
      );
    }

    case MessageSubTypes.AttachmentVersion:
      return (
        <AttachmentVersionMessage
          comment={comment}
          keyForString={keyForString}
        />
      );
    case MessageSubTypes.AttachmentCommentThread: {
      const fileExtension = extname(
        comment.meta.attachment.location || comment.meta.attachment.name,
      );
      const isImage = isImageUrlFromExtension(
        comment.meta.attachment.location || comment.meta.attachment.name,
      );

      const itemClass = classNames(
        styles.item,
        !isImage && styles.item__other,
        fileExtension === 'pdf' && styles.item__pdf,
        (fileExtension === 'csv' || fileExtension === 'xlsx') &&
          styles.item__csv,
        fileExtension === 'pptx' && styles.item__ppt,
        fileExtension === 'docx' && styles.item__docs,
      );
      const ellipsisConfig = { rows: 2 };

      return (
        <AttachmentCommentWrapper>
          <FormattedMessage
            id={keyForString}
            values={{
              fileName: comment?.meta?.attachment?.name,
              changedByUserName: comment?.meta?.changedByUserName,
            }}
          />
          {comment.meta.changeType === 'REMOVED' ? null : (
            <AttachmentMetaWrapper>
              {isImage ? (
                <AttachmentThumbnail
                  src={comment.meta.attachment.location}
                  alt={comment.meta.attachment.name}
                  height="100px"
                  width="100%"
                />
              ) : (
                <div className={itemClass} role="button" tabIndex={0}>
                  <h6 className="h6">{fileExtension}</h6>
                </div>
              )}
              <AttachmentMetaTextWrapper>
                <Flex>
                  <span>&quot;</span>
                  <Paragraph ellipsis={ellipsisConfig}>
                    <span
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        __html: comment.meta.comment.content,
                      }}
                    />
                  </Paragraph>
                  <span>&quot;</span>
                </Flex>
                <Link
                  to={RoutePaths.attachmentWorkspace.replace(
                    ':attachmentId',
                    comment.meta.attachment.attachmentId.toString(),
                  )}
                >
                  <FormattedMessage id="view_annotation" />
                </Link>
              </AttachmentMetaTextWrapper>
            </AttachmentMetaWrapper>
          )}
        </AttachmentCommentWrapper>
      );
    }

    case MessageSubTypes.AttachmentCommentThreadResolve:
    case MessageSubTypes.AttachmentComment:
      return (
        <FormattedMessage
          id={keyForString}
          values={{
            fileName: comment?.meta?.attachment?.name,
            changedByUserName: comment?.meta?.changedByUserName,
          }}
        />
      );

    case MessageSubTypes.TaskCsatAdded:
      return (
        <FormattedMessage
          id={keyForString}
          values={{ changedByUserName: fullName(userDetails) }}
        />
      );

    default:
      return <></>;
  }
};

const getUserInfoForSubtypes = (
  comment: IControlMesssage,
  userDetails: User,
): undefined | User => {
  const subType = comment.messageSubType;

  if (subType === MessageSubTypes.ProjectConversationNew) {
    return userDetails;
  }

  if (subType === MessageSubTypes.ProjectConversationNewMember) {
    return (
      (comment as ProjectConversationNewMember).meta?.users &&
      (comment as ProjectConversationNewMember).meta?.users[0]
    );
  }

  return undefined;
};

export const ControlMessage: FC<AuditLogProps> = ({
  className = '',
  comment,
}) => {
  const intl = useIntl();
  const userDetails = useExternalIntegrationUserDetails(
    comment.commentMeta?.externalUser,
    comment.user,
  );

  const metaUser = getUserInfoForSubtypes(comment, userDetails);

  try {
    return metaUser ? (
      <CommentWrapper
        className={className}
        author={<strong>{fullName(metaUser)}</strong>}
        avatar={
          <UserMessageCommentAvatar
            avatarUrl={metaUser.photoUrl}
            createdAt={comment.createdAt}
            isFromSameUser
            userFullName={fullName(metaUser)}
          />
        }
        content={<small>{renderText(comment, intl, userDetails)}</small>}
      />
    ) : (
      <CommentWrapper
        className={className}
        avatar={
          <Date>
            <FormattedTime value={comment.createdAt} />
          </Date>
        }
        content={<small>{renderText(comment, intl, userDetails)}</small>}
      />
    );
  } catch (e) {
    return null;
  }
};
